
import axios, {Method} from "axios";
import {ResourceInvalidationTriggers} from "@k/global/resourcemanager/resourceManager";
import {ResourceLoaderConstructorArguments} from "@k/global/resourcemanager/resourceLoader";
import {
    ResourceManagerTask,
    ResourceManagerTaskPrerequisite
} from "@k/global/resourcemanager/task";
import {Resource} from "@k/global/resourcemanager/resource";
import {PalettenCluster, PalettenEintragFromDBWithFarbgruppenEinteilung} from "@k/js/databaseInterfaceClasses";
import {usePalettenClusterStore} from "@k/vue/store/palettenClusterStore";
import {KeyValueResourceContainer} from "@km/store/KeyValueResourceContainer";

const startLoadingStatus = "loading";
const loadedStatus = "loaded"

export const PALETTEN_RESOURCE_PREFIX = "palette_";
export const PALETTEN_RESOURCE_SORTED_PREFIX = "palette_inGroups_";
export const PALETTENCLUSTER_RESOURCE_PREFIX = "palettenCluster";
const getClusterResourceName = (clustername:string)=>[PALETTENCLUSTER_RESOURCE_PREFIX, clustername].join("_");




function createPalettenResources(palettenCluster:PalettenEintragFromDBWithFarbgruppenEinteilung[][], palettenClusterSortedIntoGroups:PalettenEintragFromDBWithFarbgruppenEinteilung[][][]) {
    const out = [] as Resource[];
    const getpalettenResource = (resourceName, data)=>new Resource({
        resourceName, startingStati:["loaded"], data, invalidatedWhen:ResourceInvalidationTriggers.USERCHANGED})

    for(const palette in palettenCluster) {
        out.push(getpalettenResource(PALETTEN_RESOURCE_SORTED_PREFIX+palette, palettenClusterSortedIntoGroups[palette]))
        out.push(getpalettenResource(PALETTEN_RESOURCE_PREFIX+palette, palettenCluster[palette]));
    }
    return out;
}

type ResourceStoreState = {resources:KeyValueResourceContainer};
export default {
    state: ()=>({
        resources: new KeyValueResourceContainer()
    }),
    getters: {
        resourceExists: (state:ResourceStoreState):(string)=>boolean => resourceName => state.resources.exists(resourceName),
        getResourceByName: (state:ResourceStoreState):(string)=>Resource|null => resourceName => {
            return state.resources.get(resourceName)
        },
        taskPrerequisitesMet: (state:ResourceStoreState) => function (task: ResourceManagerTask) {
            return task.prerequisites.reduce(function(carry, prerequisite:ResourceManagerTaskPrerequisite) {
                const resource = state.resources.get(prerequisite.resourceName);
                return (resource === null || !resource.meetsRequirements(prerequisite.prerequisites)) ? false : carry
            }, true);
        },
        getInvalidatedResources: (state:ResourceStoreState) => eventName => state.resources.filterResources(r => r.invalidatedWhen.includes(eventName)),
        resourceHasState: (state:ResourceStoreState, getters) => (resourceName: string, statetoTest: string) => {
            const resource = getters.getResourceByName(resourceName);
            if (resource === null) {
                return false;
            }
            return resource.hasState(statetoTest);
        },
        getDataForTask: (state:ResourceStoreState, getters) => (task: ResourceManagerTask) => {
            return task.prerequisites.reduce(function(carry, prerequisite:ResourceManagerTaskPrerequisite) {
                const resourceState = getters.getResourceByName(prerequisite.resourceName);
                if (resourceState !== null) {
                    carry[prerequisite.resourceName] = resourceState.data;
                }
                return carry;
            }, {})
        },
        getPalettenCluster: (state:ResourceStoreState, getters) => (clustername) => {
            const clusterResource = getters.getResourceByName(getClusterResourceName(clustername));
            return clusterResource !== null ? clusterResource.data : null;
        },
        getPalettenClusterSorted: (state:ResourceStoreState, getters) => (palettenCluster:PalettenCluster) => {
            const palettenNamen = [] as string[];
            for(const palette in palettenCluster) {
                palettenNamen.push(palette);
            }
            return palettenNamen.reduce((carry, p)=>{
                const paletteSorted = getters.getResourceByName(PALETTEN_RESOURCE_SORTED_PREFIX+p)?.data || null;
                if(paletteSorted === null) {
                    return carry;
                }
                carry[p] = paletteSorted;
                return carry;
            }, [])
        },
    },
    mutations: {
        deleteResource: (state:ResourceStoreState, resourcename) => state.resources.delete(resourcename),
        appendStatus(state:ResourceStoreState, payload: { resourceName, newStatus }) {
            const resource = state.resources.get(payload.resourceName);
            if (resource === null) {
                return;
            }
            resource.addState(payload.newStatus);

        },
        addResource(state:ResourceStoreState, resource: Resource) {
            state.resources.add(resource);
        },
        registerResource(state:ResourceStoreState, {resourceName, startingStati, data, invalidatedWhen}) {
            const resource = new Resource({resourceName, startingStati, data, invalidatedWhen})
            state.resources.add(resource);
        },
        addDataToResource(state:ResourceStoreState, payload) {
            const resource = state.resources.get(payload.resourceName);
            if (resource === null) {
                console.error("Resource " + payload.resourceName + " nicht gefunden");
                return;
            }
            resource.data = payload.data;
        },
        PALETTENCLUSTER_LOADED: (state:ResourceStoreState, data:{palettenclusters, sortedIntoGroups}) => {

            const store = usePalettenClusterStore();
            for (const cluster in data.palettenclusters) {
                const resource = state.resources.get(getClusterResourceName(cluster));

                if(resource === null) {
                    console.error("palettencluster Ladefehler : "+cluster + " nicht gefunden");
                    return;
                }
                store.setPalettenCluster(cluster, data.palettenclusters[cluster]);
                resource.data = data.palettenclusters[cluster];
                createPalettenResources(data.palettenclusters[cluster], data.sortedIntoGroups[cluster]).forEach(r=>state.resources.add(r));
                resource.addState("loaded");
            }
        },
        LOADING_PALETTENCLUSTER: (state:ResourceStoreState, clusternames) => {
            clusternames.forEach(cn=>{
                state.resources.add(new Resource({
                    resourceName:getClusterResourceName(cn),
                    startingStati:["loading"],
                    invalidatedWhen:ResourceInvalidationTriggers.USERCHANGED
                    }
                ));
            })
        },
    },
    actions: {
        deleteInvalidatedResources(context, eventName) {
            const invalid = context.getters.getInvalidatedResources(eventName);
            invalid.forEach(r => context.commit("deleteResource", r.key))
        },

        loadResource(context, args: ResourceLoaderConstructorArguments) {
            context.commit("appendStatus", {resourceName: args.resourceName, newStatus: startLoadingStatus});
            axios({
                url: args.url,
                method: args.method as Method,
                data: args.data
            }).then(
                (response) => {
                    context.commit("addDataToResource", {resourceName: args.resourceName, data: response.data});
                    context.commit("appendStatus", loadedStatus)
                })
        },
        loadPalettenClusters(context, palettenClusterArray) {
            const actuallyLoad = palettenClusterArray.filter(cn => context.getters.getPalettenCluster(cn) === null);
            if (actuallyLoad.length === 0) {
                return;
            }
            context.commit("LOADING_PALETTENCLUSTER", actuallyLoad);
            const url = "/konfigurator_php/designmodul/colors/loadPalettenClusterGrouped.php";
            const toSend = new FormData();
            toSend.append("sport", "Bike");
            toSend.append("palettenClusters", JSON.stringify(actuallyLoad));

            axios({
                method: "post",
                url: url,
                headers: {'content-type': 'application/x-www-form-urlencoded'},
                data: {
                    sport:context.getters["ModelStore/getAuwiSportFuerProduktAuswahl"],
                    palettenClusters:JSON.stringify(actuallyLoad)
                }

            })
                .then(response => {
                    context.commit("PALETTENCLUSTER_LOADED", response.data);
                })
        }
    }
}
